<template>
  <div id="hydrogen-2023-projects" style="width: 100%; height: 100%"></div>
</template>

<script>
export default {
  name: "TheMap",
  props: {
    id: String,
    map: Object,
    data: Array,
    popup: Object,
    showMiscLayer: Boolean,
    selectedRange: Array,
    deselectedStatus: Array,
    deselectedTech: Array,
    selectedCountries: Array,
    techColors: Object,
  },
  watch: {
    isLoaded(loaded) {
      if (loaded) this.initmap();
    },
    showMiscLayer(curr) {
      this.reloadMap("misc", curr);
    },
    selectedRange() {
      this.reloadMap();
    },
    deselectedStatus() {
      this.reloadMap();
    },
    deselectedTech() {
      this.reloadMap();
    },
    selectedCountries() {
      this.reloadMap();
    },
  },
  computed: {
    isLoaded() {
      return this.map && this.data ? true : false;
    },

    defaultFilter() {
      const { selectedRange, noyearFilter } = this;
      const [min, max] = selectedRange;
      let temp = [
        "all",
        ["<=", ["get", "indexedYear"], Number(max)],
        [">=", ["get", "indexedYear"], Number(min)],
      ];
      if (noyearFilter) {
        temp.push(...noyearFilter);
      }
      return [...temp];
    },
    noyearFilter() {
      const { techandstatusfilter, countryFilter } = this;
      let temp = [];
      if (techandstatusfilter) {
        temp.push(...techandstatusfilter);
      }
      if (countryFilter) {
        temp.push(countryFilter);
      }
      return temp.length === 0 ? false : temp;
    },
    techandstatusfilter() {
      const { deselectedTech, deselectedStatus } = this;
      let techfilter = [
        "match",
        ["get", "technology"],
        deselectedTech,
        false,
        true,
      ];
      let statusfilter = [
        "match",
        ["get", "status"],
        deselectedStatus,
        false,
        true,
      ];
      if (deselectedTech.length > 0) {
        if (deselectedStatus.length > 0) return [techfilter, statusfilter];
        return [techfilter];
      }
      if (deselectedStatus.length > 0) {
        if (deselectedTech.length > 0) return [techfilter, statusfilter];
        return [statusfilter];
      }
      return false;
    },
    countryFilter() {
      const { selectedCountries } = this;
      return selectedCountries.length > 0
        ? ["match", ["get", "country"], selectedCountries, true, false]
        : false;
    },
  },
  methods: {
    initmap() {
      const { map, data, defaultFilter, techColors } = this;
      map.on("load", () => {
        map
          .setLayoutProperty("disputed-borders", "visibility", "none")
          .setLayoutProperty("disputed-borders-overlay", "visibility", "none")
          .setLayoutProperty("borders", "visibility", "none");
        map
          .addSource("hydrogen-withyear-source", {
            type: "geojson",
            generateId: true,
            data: {
              type: "FeatureCollection",
              features: data.filter((d) => d.properties.hasYear),
            },
          })
          .addSource("hydrogen-without-source", {
            type: "geojson",
            generateId: true,
            data: {
              type: "FeatureCollection",
              features: data.filter((d) => !d.properties.hasYear),
            },
          })
          .addLayer({
            id: "hydrogen-withyear-layer",
            type: "circle",
            source: "hydrogen-withyear-source",
            filter: defaultFilter,
            paint: {
              "circle-color": [
                "case",
                ["boolean", ["feature-state", "hover"], false],
                "#000",
                [
                  "match",
                  ["get", "technology"],
                  ["Electrolysis"],
                  techColors["Electrolysis"].color,
                  ["Fossil fuels with CCUS"],
                  techColors["Fossil fuels with CCUS"].color,
                  techColors["Other"].color,
                ],
              ],
              "circle-opacity": 0.8,
              "circle-stroke-color": "#000",
              "circle-stroke-width": 0.5,
              "circle-radius": [
                "interpolate",
                ["linear"],
                ["zoom"],
                1.5,
                [
                  "case",
                  ["<=", ["get", "capacity_kt"], 50],
                  2,
                  ["<=", ["get", "capacity_kt"], 150],
                  4,
                  ["<", ["get", "capacity_kt"], 250],
                  6,
                  ["<=", ["get", "capacity_kt"], 500],
                  8,
                  ["<", ["get", "capacity_kt"], 1000],
                  10,
                  ["<=", ["get", "capacity_kt"], 5000],
                  12,
                  20,
                ],
                6,
                [
                  "case",
                  ["<=", ["get", "capacity_kt"], 50],
                  2,
                  ["<=", ["get", "capacity_kt"], 150],
                  4,
                  ["<", ["get", "capacity_kt"], 250],
                  6,
                  ["<=", ["get", "capacity_kt"], 500],
                  8,
                  ["<", ["get", "capacity_kt"], 1000],
                  10,
                  ["<=", ["get", "capacity_kt"], 5000],
                  12,
                  20,
                ],
              ],
            },
          })
          .addLayer({
            id: "hydrogen-without-layer",
            type: "circle",
            source: "hydrogen-without-source",
            layout: { visibility: "none" },
            paint: {
              "circle-color": "#fff",
              "circle-stroke-width": 2,
              "circle-stroke-color": [
                "case",
                ["boolean", ["feature-state", "hover"], false],
                "#000",
                [
                  "match",
                  ["get", "technology"],
                  ["Electrolysis"],
                  techColors["Electrolysis"].color,
                  ["Fossil fuels with CCUS"],
                  techColors["Fossil fuels with CCUS"].color,
                  techColors["Other"].color,
                ],
              ],
              "circle-opacity": 0.75,
              "circle-radius": [
                "interpolate",
                ["linear"],
                ["zoom"],
                2,
                1,
                6,
                5,
              ],
            },
          });
        let withId = null;
        let withoutId = null;
        map
          .on("mousemove", "hydrogen-withyear-layer", (e) => {
            if (withId) {
              map.removeFeatureState({
                source: "hydrogen-withyear-source",
                id: withId,
              });
            }
            withId = e.features[0].id;
            map.setFeatureState(
              { source: "hydrogen-withyear-source", id: withId },
              { hover: true }
            );
            this.mousemove(e);
          })
          .on("mousemove", "hydrogen-without-layer", (e) => {
            if (withoutId) {
              map.removeFeatureState({
                source: "hydrogen-without-source",
                id: withoutId,
              });
            }
            withoutId = e.features[0].id;
            map.setFeatureState(
              { source: "hydrogen-without-source", id: withoutId },
              { hover: true }
            );
            this.mousemove(e);
          })
          .on("mouseleave", "hydrogen-withyear-layer", (e) => {
            if (withId || withId === 0) {
              map.setFeatureState(
                { source: "hydrogen-withyear-source", id: withId },
                { hover: false }
              );
            }
            withId = null;
            this.mouseleave(e);
          })
          .on("mouseleave", "hydrogen-without-layer", (e) => {
            if (withoutId || withoutId === 0) {
              map.setFeatureState(
                { source: "hydrogen-without-source", id: withoutId },
                { hover: false }
              );
            }
            withoutId = null;
            this.mouseleave(e);
          });
      });
    },
    mousemove(e) {
      const { map, popup, techColors } = this;
      if (e.features.length === 0) return;
      map.getCanvas().style.cursor = "pointer";
      let mousePos = [e.lngLat.lng, e.lngLat.lat];
      let node = e.features[0].properties;
      let result = `<div class="hydrogen-projects-tooltip">
          <div class="tooltip-title">${node.name}</div>
          <div class="tooltip-content">
            ${
              node.capacity_kt
                ? "Project capacity: <span>" +
                  this.formatValue(node.capacity_kt) +
                  "</span> kt H2/yr <br/>"
                : ""
            }
            ${node.status && "Status: <span>" + node.status}</span><br/>
            ${
              node.technology &&
              "Technology: <span style=color:" +
                techColors[node.technology].color +
                ">" +
                node.technology
            }</span>
          </div>
        </div>`;
      popup.setLngLat(mousePos).setHTML(result).addTo(map);
    },
    mouseleave() {
      const { map, popup } = this;
      map.getCanvas().style.cursor = "";
      popup.remove();
    },
    formatValue(value) {
      return value >= 0.1 && value < 1
        ? value.toFixed(2)
        : value < 0.1
        ? "< 0.01"
        : Math.round(value);
    },
    reloadMap(type, value) {
      const { map, defaultFilter, noyearFilter } = this;
      if (type === "misc") {
        map.setLayoutProperty(
          "hydrogen-without-layer",
          "visibility",
          value ? "visible" : "none"
        );
      }

      //Filter layer with time
      map.setFilter("hydrogen-withyear-layer", defaultFilter);

      //Filter layer without time
      if (!noyearFilter) {
        map.setFilter("hydrogen-without-layer", null);
      } else {
        map.setFilter("hydrogen-without-layer", ["all", ...noyearFilter]);
      }
    },
  },
};
</script>

<style>
.hydrogen-projects-tooltip .tooltip-title {
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 0.875rem;
}
.hydrogen-projects-tooltip .tooltip-content {
  margin-top: 8px;
}
.hydrogen-projects-tooltip .tooltip-content span {
  font-weight: 600;
}
.hydrogen-projects-tooltip {
  font-size: 0.875rem;
  line-height: 0.95rem;
  font-family: Graphik;
}
</style>
